import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Dropdown from 'components/dropdown';
import FieldLabel from 'components/FieldLabel';

import IconArrowDown from '../icons/icon-arrow-down';

export default class Select extends Component {
    static propTypes = {
        placeholder: PropTypes.any,
        selected: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        options: PropTypes.array,
        label: PropTypes.string,
        icon: PropTypes.object,
        onSelect: PropTypes.func,
        onChange: PropTypes.func,
        onDropdownShow: PropTypes.func,
        onDropdownHide: PropTypes.func
    };

    static defaultProps = {
        placeholder: null,
        options: [],
        dropdownClassname: ''
    };

    constructor(props) {
        super(props);

        this.state = {
            selectedOption: this.props.selected ?? (this.props.placeholder ? null : (this.props.options[0] || {}).id),
            dropdownVisible: false
        };

        this.dropdownCtrl = null;
    }

    handleOptionClick = option => {
        this.props.onSelect && this.props.onSelect(option);
        this.props.onChange && this.props.onChange(option);

        this.setState({
            selectedOption: option.id
        });

        if (option.onSelect) {
            option.onSelect(option, () => this.dropdownCtrl && this.dropdownCtrl.hide());
        } else {
            this.dropdownCtrl && this.dropdownCtrl.hide();
        }

    }

    handleToggleClick = event => {
        this.toggleDropdown();
    }

    handleDropdownShow = () => {
        this.setState({
            dropdownVisible: true
        });

        this.props.onDropdownShow && this.props.onDropdownShow();
    }

    handleDropdownHide = () => {
        this.setState({
            dropdownVisible: false
        });

        this.props.onDropdownHide && this.props.onDropdownHide();
    }

    toggleDropdown() {
        if (this.state.dropdownVisible) {
            this.hideDropdown();
        } else {
            this.showDropdown();
        }
    }

    showDropdown() {
        if (this.state.dropdownVisible) {
            return;
        }

        this.dropdownCtrl.show();

        const documentClickHandler = event => {
            if (event.target.nodeName === 'INPUT' && event.target.closest('.select-dropdown')) {
                return;
            }

            this.hideDropdown();

            document.removeEventListener('click', documentClickHandler);
        };

        setTimeout(() => {
            document.addEventListener('click', documentClickHandler);
        }, 0);
    }

    hideDropdown() {
        if (!this.state.dropdownVisible) {
            return;
        }

        if (this.dropdownCtrl) {
            this.dropdownCtrl.hide();   
        }
    }

    getWidth = () => {
        return (this.element && this.element.clientWidth) || null;
    }

    get label() {
        if (this.props.label) {
            return <FieldLabel id={this.props.id} dark={this.props.darkLabel} required={this.props.required}>{this.props.label}</FieldLabel>
        }

        return null;
    }

    get selected() {
        return this.props.options.find(option => option.id === this.state.selectedOption);
    }

    get selectedElement() {
        if (this.props.selectedComponent) {
            return React.cloneElement(this.props.selectedComponent, { ...this.selected });
        }

        return (
            <React.Fragment>
                {
                    (this.props.icon || this.selected.icon) && (
                        <span className="icon">
                            { this.props.icon || this.selected.icon }
                        </span>
                    )
                }

                {
                    this.props.selectedPrefix ? <span className="prefix">{this.props.selectedPrefix}</span> : null
                }

                {
                    this.selected && this.selected.text
                }
            </React.Fragment>
        );
    }

    componentDidMount() {
        const ctrl = {
            getValue: () => {
                return this.state.selectedOption;
            }
        };

        if (this.props.provideController) {
            if ('current' in this.props.provideController) {
                this.props.provideController.current = ctrl;
            } else {
                this.props.provideController && this.props.provideController(ctrl);
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.selected !== nextProps.selected) return true;
        if (this.props.label !== nextProps.label) return true;
        if (this.props.icon !== nextProps.icon) return true;
        if (this.props.options !== nextProps.options) return true;
        if (this.state !== nextState) return true;

        return false;
    }

    componentDidUpdate(prevProps) {
        if (this.props.options && this.props.options.length && !this.props.options.some(option => option.id === this.state.selectedOption)) {
            if (this.props.selected) {
                if (this.props.selected !== this.state.selectedOption) {
                    this.setState({
                        selectedOption: this.props.selected
                    });
                }
            } else if (!this.props.placeholder) {
                this.setState({  
                    selectedOption: this.props.options[0].id
                });
            }
        } else if (this.props.selected !== prevProps.selected && this.state.selectedOption !== this.props.selected) {
            this.setState({
                selectedOption: this.props.selected
            });
        }
    }

    componentWillUnmount() {
        if (this.props.provideController) {
            if ('current' in this.props.provideController) {
                this.props.provideController.current = null;
            } else {
                this.props.provideController && this.props.provideController(null);
            }
        }
    }

    render() {
        return (
            <div className="field-holder">
                { this.label }

                <div ref={element => this.element = element} className={'field field-default select' + (this.state.dropdownVisible ? ' dropdown-visible' : '') + (this.props.small ? ' small' : '') + (this.props.xsmall ? ' xsmall' : '') + (!this.selected ? ' placeholder' : '')}>
                    <div className="select-toggle field-input" onClick={this.handleToggleClick}>
                        <div className="selection">
                            {
                                this.selected ? this.selectedElement : this.props.placeholder
                            }
                        </div>

                        <span className="arrow">
                            <IconArrowDown />
                        </span>
                    </div>

                    <Dropdown
                        provideController={ctrl => {this.dropdownCtrl = ctrl}}
                        className={'select-dropdown ' + this.props.dropdownClassname}
                        withSearch={this.props.withSearch}
                        withoutPortal={this.props.withoutPortal}
                        items={this.props.options.map(item => ({...item, selected: item.selected ?? item.id === this.state.selectedOption}))}
                        emptyText={this.props.emptyText}
                        onShow={this.handleDropdownShow}
                        onHide={this.handleDropdownHide}
                        onItemClick={this.handleOptionClick}
                        getWidth={this.getWidth}
                        useMinWidth={true}
                        visible={this.props.visible}
                        itemComponent={this.props.itemComponent}
                        xAlign="right"
                        defaultXPosition="left"
                    />
                </div>
            </div>
        );
    }
}
